import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  UploadSimple,
  FilePdf,
  FileDoc,
  Warning,
  ShieldCheck,
  Files,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { LEVEL_META } from "../lib/risk";

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-md p-5 flex items-center gap-4 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_8px_16px_-8px_rgba(10,17,40,0.1)]"
      data-testid={testid}
    >
      <span
        className="flex items-center justify-center w-11 h-11 rounded-md shrink-0"
        style={{ background: `${color}14`, color }}
      >
        <Icon size={22} weight="fill" />
      </span>
      <div>
        <div className="font-display font-bold text-3xl text-navy leading-none">{value}</div>
        <div className="label-caps mt-1.5">{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");

  const { data: stats } = useQuery({
    queryKey: ["stats"],
    queryFn: async () => (await api.get("/stats")).data,
  });

  const handleFile = useCallback(
    (file) => {
      setError("");
      if (!file) return;
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext !== "pdf" && ext !== "docx") {
        setError(lang === "pt" ? "Envie um arquivo PDF ou DOCX." : "Please upload a PDF or DOCX file.");
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        setError(lang === "pt" ? "Arquivo excede 15 MB." : "File exceeds 15 MB.");
        return;
      }
      navigate("/processing", { state: { file } });
    },
    [navigate, lang]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <header className="animate-fade-up">
        <p className="label-caps text-gold">LEXADM</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-navy mt-2">
          {t("dash.title")}
        </h1>
        <p className="text-base text-slate-500 mt-3 max-w-2xl leading-relaxed">
          {t("dash.subtitle")}
        </p>
      </header>

      {/* Upload zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="upload-dropzone"
        className={`grid-texture mt-9 min-h-[300px] rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-center px-6 cursor-pointer transition-all duration-200 animate-fade-up ${
          dragging
            ? "border-navy bg-navy/[0.03]"
            : "border-slate-300 bg-white hover:border-navy/50 hover:bg-slate-50/50"
        }`}
        style={{ animationDelay: "60ms" }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.docx"
          className="hidden"
          data-testid="file-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <span className="flex items-center justify-center w-16 h-16 rounded-full bg-navy text-gold-bright mb-5">
          <UploadSimple size={30} weight="bold" />
        </span>
        <p className="font-display font-semibold text-2xl text-navy">{t("dash.drop")}</p>
        <p className="text-sm text-slate-400 my-2">{t("dash.or")}</p>
        <span className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold rounded-md px-5 py-2.5 hover:bg-black transition-colors duration-200">
          {t("dash.select")}
        </span>
        <div className="flex items-center gap-4 mt-6 text-slate-400">
          <span className="flex items-center gap-1.5 text-xs font-medium">
            <FilePdf size={16} weight="fill" /> PDF
          </span>
          <span className="flex items-center gap-1.5 text-xs font-medium">
            <FileDoc size={16} weight="fill" /> DOCX
          </span>
          <span className="text-xs">·</span>
          <span className="text-xs">{t("dash.accept")}</span>
        </div>
      </div>

      {error && (
        <div
          className="mt-4 flex items-center gap-2 text-sm text-risk-critical"
          data-testid="upload-error"
        >
          <Warning size={18} weight="fill" /> {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        <StatCard
          icon={Files}
          label={t("stats.total")}
          value={stats?.total ?? 0}
          color="#0A1128"
          testid="stat-total"
        />
        <StatCard
          icon={Warning}
          label={t("stats.high")}
          value={stats?.high ?? 0}
          color="#E76F51"
          testid="stat-high"
        />
        <StatCard
          icon={ShieldCheck}
          label={t("stats.low")}
          value={stats?.low ?? 0}
          color="#2A9D8F"
          testid="stat-low"
        />
      </div>

      {/* Recent */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <ClockCounterClockwise size={18} className="text-slate-400" />
          <h2 className="label-caps">{t("stats.recent")}</h2>
        </div>
        <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
          {(stats?.recent || []).length === 0 && (
            <div className="px-5 py-8 text-sm text-slate-400 text-center" data-testid="recent-empty">
              {t("hist.empty")}
            </div>
          )}
          {(stats?.recent || []).map((a) => {
            const meta = LEVEL_META[a.level] || LEVEL_META.MEDIO;
            return (
              <button
                key={a.id}
                onClick={() => navigate(`/result/${a.id}`)}
                data-testid={`recent-row-${a.id}`}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors duration-200 text-left"
              >
                <span className="text-slate-300">
                  {a.ext === "pdf" ? <FilePdf size={22} weight="fill" /> : <FileDoc size={22} weight="fill" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-navy truncate">{a.filename}</span>
                  <span className="block text-xs text-slate-400">
                    {new Date(a.created_at).toLocaleString(lang === "pt" ? "pt-BR" : "en-US")}
                  </span>
                </span>
                <span
                  className="px-2.5 py-1 rounded text-[11px] font-bold tracking-widest shrink-0"
                  style={{ color: meta.color, background: meta.bg }}
                >
                  {a.score}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
